import { useState } from "react";
import {
  vaultEntryInput,
  type VaultEntry,
  type VaultEntryInput,
  type VaultEntryUpdate,
  type VaultKind,
} from "@akhshy/types";
import type { UseResource } from "../../../lib/useResource.ts";
import { useForm } from "../../../lib/useForm.ts";
import { useSlugField } from "../../../lib/slug.ts";
import { FormField } from "../../molecules/FormField.tsx";
import { MetaFields } from "../../molecules/MetaFields.tsx";
import { MarkdownField } from "../../molecules/MarkdownField.tsx";
import { SlugField } from "../../molecules/SlugField.tsx";
import { TagInput } from "../../molecules/TagInput.tsx";
import { FormActions } from "../../molecules/FormActions.tsx";
import { Input } from "../../atoms/Input.tsx";
import { Textarea } from "../../atoms/Textarea.tsx";
import { Select } from "../../atoms/Select.tsx";

interface Props {
  initial: VaultEntry | null;
  onDone: () => void;
  resource: UseResource<VaultEntry, VaultEntryInput, VaultEntryUpdate>;
}

const KINDS: VaultKind[] = ["note", "tool", "read", "reference"];

export function VaultForm({ initial, onDone, resource }: Props) {
  const { errors, submitting, submit } = useForm(vaultEntryInput, "Vault entry saved");
  const [kind, setKind] = useState<VaultKind>(initial?.kind ?? "note");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [filename, setFilename] = useState(initial?.filename ?? "");
  const [content, setContent] = useState(initial?.content ?? "");
  const [url, setUrl] = useState(initial?.url ?? "");
  const { slug, setSlug, suggestFrom } = useSlugField(initial?.slug ?? "");
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [published, setPublished] = useState(initial?.published ?? true);
  const [sortOrder, setSortOrder] = useState(initial?.sortOrder ?? 0);

  const isNote = kind === "note";

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      kind,
      title,
      description,
      filename: isNote ? filename || null : null,
      content: isNote ? content : null,
      url: isNote ? null : url,
      slug: slug || undefined,
      tags,
      published,
      sortOrder,
    };
    void submit(payload, async (parsed) => {
      if (initial) await resource.update(initial.id, parsed);
      else await resource.create(parsed);
      onDone();
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="field-row">
        <FormField label="Kind" error={errors["kind"]}>
          <Select value={kind} onChange={(e) => setKind(e.target.value as VaultKind)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Title" error={errors["title"]}>
          <Input
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              suggestFrom(e.target.value);
            }}
          />
        </FormField>
      </div>
      <SlugField slug={slug} onSlug={setSlug} error={errors["slug"]} />
      <FormField label="Description" error={errors["description"]}>
        <Textarea value={description} rows={2} onChange={(e) => setDescription(e.target.value)} />
      </FormField>
      {isNote ? (
        <>
          <FormField label="Filename" error={errors["filename"]}>
            <Input value={filename} placeholder="docker-cleanup.md" onChange={(e) => setFilename(e.target.value)} />
          </FormField>
          <MarkdownField
            label="Content (markdown)"
            value={content}
            onChange={setContent}
            rows={12}
            error={errors["content"] ?? errors[""]}
          />
        </>
      ) : (
        <FormField label="URL" error={errors["url"] ?? errors[""]}>
          <Input value={url} placeholder="https://example.com" onChange={(e) => setUrl(e.target.value)} />
        </FormField>
      )}
      <TagInput value={tags} onChange={setTags} error={errors["tags"]} />
      <MetaFields
        published={published}
        sortOrder={sortOrder}
        onPublished={setPublished}
        onSortOrder={setSortOrder}
        error={errors["sortOrder"]}
      />
      <FormActions submitting={submitting} editing={initial !== null} onCancel={onDone} />
    </form>
  );
}
